import type { DrawingPreferences } from '@shared/types';
import { ChipSelect } from '@/components/intake/fields/ChipSelect';
import { ToggleChip } from '@/components/intake/fields/ToggleChip';
import { useTranslation } from 'react-i18next';

const SCOPE_VALUES: Array<DrawingPreferences['scope']> = ['assembly_only', 'parts_only', 'both'];

const SECTION_VALUES: Array<DrawingPreferences['sectionCuts']> = ['auto', 'none'];

const TOLERANCE_VALUES: Array<DrawingPreferences['toleranceClass']> = ['loose', 'standard', 'tight', 'precision'];

const DEFAULT: DrawingPreferences = {
  scope: 'assembly_only',
  sectionCuts: 'auto',
  showHoleTable: false,
  showBOM: false,
  toleranceClass: 'standard',
  jisStrict: false,
};

export function DrawingSection({
  value,
  onChange,
}: {
  value: DrawingPreferences | undefined;
  onChange: (next: DrawingPreferences) => void;
}) {
  const { t } = useTranslation('intake');
  const v: DrawingPreferences = value ?? DEFAULT;
  const update = (next: Partial<DrawingPreferences>) => onChange({ ...v, ...next });
  const scopeOptions = SCOPE_VALUES.map((scope) => ({
    value: scope,
    label: t(`drawingPrefs.scopes.${scope}`),
  }));
  const sectionOptions = SECTION_VALUES.map((section) => ({
    value: section,
    label: t(`drawingPrefs.sections.${section}`),
  }));
  const toleranceOptions = TOLERANCE_VALUES.map((tolerance) => ({
    value: tolerance,
    label: t(`drawingPrefs.tolerances.${tolerance}`),
  }));

  return (
    <div className="flex flex-col gap-3">
      <div>
        <label className="text-[10px] text-synergy-text-tertiary">{t('drawingPrefs.scope')}</label>
        <div className="mt-1">
          <ChipSelect options={scopeOptions} value={v.scope} onChange={(scope) => update({ scope })} />
        </div>
      </div>
      <div>
        <label className="text-[10px] text-synergy-text-tertiary">{t('drawingPrefs.section')}</label>
        <div className="mt-1">
          <ChipSelect options={sectionOptions} value={v.sectionCuts} onChange={(sectionCuts) => update({ sectionCuts })} />
        </div>
      </div>
      <div>
        <label className="text-[10px] text-synergy-text-tertiary">{t('drawingPrefs.tolerance')}</label>
        <div className="mt-1">
          <ChipSelect options={toleranceOptions} value={v.toleranceClass} onChange={(toleranceClass) => update({ toleranceClass })} />
        </div>
      </div>
      <div className="flex flex-wrap gap-2 pt-1">
        <ToggleChip value={v.showHoleTable} onChange={(showHoleTable) => update({ showHoleTable })} label={t('drawingPrefs.holeTable')} />
        <ToggleChip value={v.showBOM} onChange={(showBOM) => update({ showBOM })} label={t('drawingPrefs.bom')} />
        <ToggleChip value={v.jisStrict} onChange={(jisStrict) => update({ jisStrict })} label={t('drawingPrefs.jisStrict')} />
      </div>
    </div>
  );
}
